import dayjs from 'dayjs';
import { collection, doc, onSnapshot, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { AlertCircle, CheckCircle, Clock, Phone, User, Wifi, Server, ChevronUp, ChevronDown, ArrowUpDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Loading } from '../components/common/Loading';
import CountdownDisplay from '../components/countdown/CountdownDisplay';
import DelayedBadge from '../components/countdown/DelayedBadge';
import Navbar from '../components/header/Navbar';
import { useAuth } from '../hooks/useAuth';
import { db } from '../network/firebaseConfig';

const MyComplaints = () => {
    const { user, loading: isLoading, users } = useAuth();
    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });
    const [stats, setStats] = useState({
        total: 0,
        urgent: 0,
        delayed: 0,
        completed: 0,
        paused: 0
    });
    const navigate = useNavigate();

    // Helper function to get value from onu/router (handle arrays)
    const getStatusValue = (value) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value[0] : '-';
        }
        return value || '-';
    };

    // Handle phone call
    const handlePhoneClick = (phoneNumber) => {
        if (!phoneNumber) return;
        if (window.confirm(`Do you want to call ${phoneNumber}?`)) {
            window.location.href = `tel:${phoneNumber}`;
        }
    };

    useEffect(() => {
        if (!user) return;

        const q = query(
            collection(db, "complaints"),
            where("assignedTo", "==", user.uid)
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const allComplaints = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                allComplaints.sort(
                    (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
                );

                // Calculate stats
                const now = new Date();
                let urgent = 0;
                let delayed = 0;
                let completed = 0;
                let paused = 0;

                allComplaints.forEach(c => {
                    if (c.status === 'completed') completed++;
                    if (c.status === 'paused') paused++;

                    const hoursMatch = c.approximateResolveTime?.match(/(\d+)/);
                    if (hoursMatch && c.updatedAt && c.status === 'in-operation') {
                        const totalHours = parseInt(hoursMatch[1]);
                        const updatedAt = c.updatedAt?.toDate?.() || new Date(c.updatedAt);
                        const workingHours = 13;
                        const modifiedHours = Math.min(totalHours, workingHours);
                        const resolveTime = new Date(updatedAt.getTime() + modifiedHours * 60 * 60 * 1000);

                        if (now > resolveTime) {
                            delayed++;
                        } else {
                            const remaining = resolveTime - now;
                            const hoursRemaining = remaining / (1000 * 60 * 60);
                            if (hoursRemaining < 1) {
                                urgent++;
                            }
                        }
                    }
                });

                setStats({
                    total: allComplaints.length,
                    urgent,
                    delayed,
                    completed,
                    paused
                });

                setComplaints(allComplaints);
                setLoading(false);
            },
            (err) => {
                console.error(err);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Apply sorting
    useEffect(() => {
        if (sortConfig.key && complaints.length > 0) {
            const sorted = [...complaints];
            sorted.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (sortConfig.key === 'customerName') {
                    aVal = a.customerInfo?.name || '';
                    bVal = b.customerInfo?.name || '';
                } else if (sortConfig.key === 'ticketNumber') {
                    aVal = a.ticketNumber || '';
                    bVal = b.ticketNumber || '';
                } else if (sortConfig.key === 'category') {
                    aVal = a.category || '';
                    bVal = b.category || '';
                }

                if (aVal === undefined || aVal === null) aVal = '';
                if (bVal === undefined || bVal === null) bVal = '';

                if (typeof aVal === 'string') {
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
            setComplaints(sorted);
        }
    }, [sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortIndicator = ({ column }) => {
        if (sortConfig.key !== column) {
            return <ArrowUpDown className="w-3 h-3 text-gray-400 inline ml-1" />;
        }
        return sortConfig.direction === 'asc'
            ? <ChevronUp className="w-3 h-3 text-pink-500 inline ml-1" />
            : <ChevronDown className="w-3 h-3 text-pink-500 inline ml-1" />;
    };

    // Handle status update
    const handleStatusUpdate = async (complaintId, newStatus) => {
        if (!user) return;
        if (newStatus === 'completed' || newStatus === 'paused') {
            setUpdatingId(complaintId);
            try {
                const complaintRef = doc(db, "complaints", complaintId);
                const complaint = complaints.find(c => c.id === complaintId);
                await updateDoc(complaintRef, {
                    status: newStatus,
                    updatedAt: Timestamp.now(),
                    activityLogs: [
                        ...(complaint?.activityLogs || []),
                        {
                            time: Timestamp.now(),
                            message: `Status changed to ${newStatus} by technician`,
                            userId: user.uid,
                        },
                    ],
                });
                toast.success(`Status updated to ${newStatus}`);
            } catch (error) {
                console.error('Error updating status:', error);
                toast.error('Failed to update status');
            } finally {
                setUpdatingId(null);
            }
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            'pending': 'bg-yellow-100 text-yellow-800',
            'in-operation': 'bg-blue-100 text-blue-800',
            'paused': 'bg-gray-100 text-gray-800',
            'completed': 'bg-green-100 text-green-800'
        };
        const icons = {
            'pending': <Clock className="w-3.5 h-3.5" />,
            'in-operation': <AlertCircle className="w-3.5 h-3.5" />,
            'paused': <Clock className="w-3.5 h-3.5" />,
            'completed': <CheckCircle className="w-3.5 h-3.5" />
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
                {icons[status]}
                {status}
            </span>
        );
    };

    const renderNetworkStatus = (complaint) => {
        if (complaint.complaintType !== "Red signal") return null;

        return (
            <div className="mt-1.5 grid grid-cols-2 gap-1.5 text-[10px]">
                {/* ONU Status */}
                <div className="bg-blue-50/70 rounded p-1.5 border border-blue-100/50">
                    <div className="flex items-center gap-1 text-blue-700 font-medium text-[9px]">
                        <Server className="w-3 h-3" />
                        <span>ONU</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-gray-500 text-[8px]">PWR</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.onu?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.onu?.power)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">PON</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.onu?.pon) === 'green' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.onu?.pon)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">LAN</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.onu?.lan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.onu?.lan)}
                        </span>
                    </div>
                </div>

                {/* Router Status */}
                <div className="bg-purple-50/70 rounded p-1.5 border border-purple-100/50">
                    <div className="flex items-center gap-1 text-purple-700 font-medium text-[9px]">
                        <Wifi className="w-3 h-3" />
                        <span>Router</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-gray-500 text-[8px]">PWR</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.router?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.router?.power)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">WLAN</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.router?.wanLan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.router?.wanLan)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">WiFi</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.router?.wifi) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.router?.wifi)?.slice(0, 8)}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    if (loading || isLoading) return <Loading />;

    return (
        <>
            <Navbar user={user} />
            <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 space-y-4">
                {/* Header with Stats */}
                <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap justify-between items-center gap-3">
                        <div>
                            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <User className="w-5 h-5" />
                                My Complaints
                            </h1>
                            <p className="text-xs text-gray-500">Complaints assigned to you</p>
                        </div>
                        <button
                            onClick={() => navigate('/complaints')}
                            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                        >
                            View All Complaints
                        </button>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                            <p className="text-[10px] text-gray-500 font-medium">Total Assigned</p>
                            <p className="text-xl font-bold text-gray-800">{stats.total}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                            <p className="text-[10px] text-gray-500 font-medium">Urgent</p>
                            <p className={`text-xl font-bold ${stats.urgent > 0 ? 'text-orange-600' : 'text-gray-800'}`}>
                                {stats.urgent}
                            </p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                            <p className="text-[10px] text-gray-500 font-medium">Delayed</p>
                            <p className={`text-xl font-bold ${stats.delayed > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                                {stats.delayed}
                            </p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                            <p className="text-[10px] text-gray-500 font-medium">Completed</p>
                            <p className="text-xl font-bold text-green-600">{stats.completed}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                            <p className="text-[10px] text-gray-500 font-medium">Paused</p>
                            <p className={`text-xl font-bold ${stats.paused > 0 ? 'text-yellow-600' : 'text-gray-800'}`}>
                                {stats.paused}
                            </p>
                        </div>
                    </div>
                </div>

                {complaints.length === 0 ? (
                    <div className="bg-white rounded-lg shadow border border-gray-100 p-12 text-center">
                        <div className="text-6xl mb-4">📭</div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No complaints assigned</h3>
                        <p className="text-gray-500">You haven't been assigned any complaints yet.</p>
                    </div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="hidden lg:block bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full table-auto text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[70px]">
                                                <span className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('createdAt')}>
                                                    Date <SortIndicator column="createdAt" />
                                                </span>
                                            </th>
                                            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[90px]">
                                                <span className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('ticketNumber')}>
                                                    Ticket <SortIndicator column="ticketNumber" />
                                                </span>
                                            </th>
                                            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[100px]">
                                                <span className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('customerName')}>
                                                    Customer <SortIndicator column="customerName" />
                                                </span>
                                            </th>
                                            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[100px]">
                                                <span className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('category')}>
                                                    Category <SortIndicator column="category" />
                                                </span>
                                            </th>
                                            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[140px]">Time Left</th>
                                            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex-1 flex-grow">Description</th>
                                            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[120px]">Status</th>

                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {complaints.map((c, index) => (
                                            <tr key={c.id} className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                                                <td className="px-2 py-2 whitespace-nowrap text-gray-600 text-xs">
                                                    {dayjs(c.createdAt?.toDate()).format("DD-MM-YY")}
                                                </td>
                                                <td className="px-2 py-2 whitespace-nowrap font-mono text-xs font-semibold text-gray-700">
                                                    {c.ticketNumber}
                                                    {c.isDelayed && <DelayedBadge complaint={c} />}
                                                </td>
                                                <td className="px-2 py-2 truncate max-w-[120px]" title={c.customerInfo?.name}>
                                                    <div>
                                                        <div className="flex items-center gap-1">
                                                            <p className="text-sm font-medium text-gray-800 truncate">{c.customerInfo?.name}</p>
                                                            {c.customerInfo?.phone && (
                                                                <button
                                                                    onClick={() => handlePhoneClick(c.customerInfo.phone)}
                                                                    className="p-0.5 text-blue-500 hover:text-blue-700 transition flex-shrink-0"
                                                                    title={`Call ${c.customerInfo.phone}`}
                                                                >
                                                                    <Phone className="w-3 h-3" />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <p className="text-[12px] text-gray-700">{c.customerInfo?.phone}</p>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-2 whitespace-nowrap">
                                                    <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px] font-medium">
                                                        {c?.category || "-"}
                                                    </span>
                                                </td>

                                                <td className="px-2 py-2 min-w-[90px]">
                                                    <CountdownDisplay complaint={c} />
                                                </td>
                                                <td className="px-2 py-2">
                                                    {c?.description && (
                                                        <p className="text-xs text-gray-600 line-clamp-2">{c.description}</p>
                                                    )}
                                                    {renderNetworkStatus(c)}
                                                </td>

                                                <td className="px-2 py-2">
                                                    {c.status === 'completed' || c.status === 'paused' ? (
                                                        getStatusBadge(c.status)
                                                    ) : (
                                                        <select
                                                            value={c.status}
                                                            onChange={(e) => handleStatusUpdate(c.id, e.target.value)}
                                                            disabled={updatingId === c.id}
                                                            className={`w-full border border-gray-300 rounded px-1.5 py-1 text-xs focus:ring-1 focus:ring-blue-400 focus:border-transparent outline-none transition ${updatingId === c.id ? 'opacity-50 cursor-not-allowed' : ''
                                                                }`}
                                                        >
                                                            <option value="none">None</option>
                                                            <option value="completed">completed</option>
                                                            <option value="paused">paused</option>
                                                        </select>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden space-y-3">
                            {complaints.map((c) => (
                                <div key={c.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                                    <div
                                        className="p-3 bg-gray-50/50 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                                        onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-mono text-[10px] bg-gray-200 px-1.5 py-0.5 rounded font-semibold">
                                                    {c.ticketNumber}
                                                </span>
                                                {c.isDelayed && <DelayedBadge complaint={c} />}
                                                <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px] font-medium">
                                                    {c.category || '-'}
                                                </span>
                                            </div>
                                            <div className="mt-1 truncate">
                                                <div className="flex items-center gap-1">
                                                    <div className="font-medium text-sm text-gray-800 truncate">{c.customerInfo?.name}</div>
                                                    {c.customerInfo?.phone && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePhoneClick(c.customerInfo.phone);
                                                            }}
                                                            className="p-0.5 text-blue-500 hover:text-blue-700 transition flex-shrink-0"
                                                            title={`Call ${c.customerInfo.phone}`}
                                                        >
                                                            <Phone className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="text-[12px] text-gray-700 truncate">{c.customerInfo?.phone}</div>
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0 ml-2">
                                            <div className="mt-0.5 text-xs">
                                                <CountdownDisplay complaint={c} />
                                            </div>
                                            <svg
                                                className={`w-4 h-4 mt-1 mx-auto text-gray-400 transition-transform duration-200 ${expandedRow === c.id ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>

                                    {expandedRow === c.id && (
                                        <div className="p-3 space-y-3 bg-white">
                                            <div>
                                                <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Date</label>
                                                <span className="text-sm text-gray-700">
                                                    {dayjs(c.createdAt?.toDate()).format("DD-MM-YY")}
                                                </span>
                                            </div>
                                            {c?.description && (
                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Description</label>
                                                    <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                                        {c.description}
                                                    </p>
                                                </div>
                                            )}
                                            {renderNetworkStatus(c)}
                                            <div>
                                                <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Status</label>
                                                {c.status === 'completed' || c.status === 'paused' ? (
                                                    getStatusBadge(c.status)
                                                ) : (
                                                    <select
                                                        value={c.status}
                                                        onChange={(e) => handleStatusUpdate(c.id, e.target.value)}
                                                        disabled={updatingId === c.id}
                                                        className={`w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-400 focus:border-transparent outline-none transition bg-white ${updatingId === c.id ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                    >
                                                        <option value="none">None</option>
                                                        <option value="completed">completed</option>
                                                        <option value="paused">paused</option>
                                                    </select>
                                                )}
                                            </div>
                                            <div className="bg-gray-50/50 p-2 rounded">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-medium text-gray-600">⏱ Time Remaining</span>
                                                    <CountdownDisplay complaint={c} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default MyComplaints;